// IndexedDB store for guest visit photos (#107, the web counterpart to iOS's
// GuestPhotoStore, #14 Phase 1b). Guest visit drafts live in localStorage, which
// can't hold image blobs — so the (already downscaled + metadata-stripped) JPEGs
// are stashed here, keyed by the draft's id, and uploaded when the guest signs in
// and their visit drafts merge (see mergeGuestDrafts).
//
// One record per draft: { draftId, photos: [{ position, blob }, ...] } (#115),
// where `position` is the dish's index in the visit's dishes list (null = an
// un-attributed "other" photo). Legacy records shaped { draftId, blobs: [Blob] }
// are read back as position-null photos.

const DB_NAME = 'dynerz_guest';
const STORE = 'visit_photos';
const VERSION = 1;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === 'undefined') {
      reject(new Error('IndexedDB unavailable'));
      return;
    }
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE, { keyPath: 'draftId' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function withStore(mode, fn) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const t = db.transaction(STORE, mode);
      const req = fn(t.objectStore(STORE));
      t.oncomplete = () => resolve(req ? req.result : undefined);
      t.onerror = () => reject(t.error);
      t.onabort = () => reject(t.error);
    });
  } finally {
    db.close();
  }
}

/** Stash prepared JPEG photos for a guest visit draft (#115). Best-effort.
 *  `photos` = [{ position, blob }] — position is the dish index, null = "other". */
export async function saveGuestPhotos(draftId, photos) {
  if (!draftId || !photos || !photos.length) return;
  try {
    await withStore('readwrite', (s) => s.put({ draftId, photos }));
  } catch (error) {
    console.error('Failed to stash guest visit photos:', error);
  }
}

/** Return the stashed photos for a draft as [{ position, blob }] (empty on none).
 *  Reads legacy { blobs:[Blob] } records as position-null photos. */
export async function getGuestPhotos(draftId) {
  if (!draftId) return [];
  try {
    const rec = await withStore('readonly', (s) => s.get(draftId));
    if (rec && Array.isArray(rec.photos)) return rec.photos;
    if (rec && Array.isArray(rec.blobs)) return rec.blobs.map((blob) => ({ position: null, blob }));
    return [];
  } catch (error) {
    console.error('Failed to read guest visit photos:', error);
    return [];
  }
}

/** Drop a draft's stashed photos (after upload, or when the draft is discarded). */
export async function deleteGuestPhotos(draftId) {
  if (!draftId) return;
  try {
    await withStore('readwrite', (s) => s.delete(draftId));
  } catch (error) {
    console.error('Failed to delete guest visit photos:', error);
  }
}
